import {
	ConfigItemSelectionOption,
	ConfigurationItemProperty,
} from '../bos/configuration-item-property.js'
import { AiType, AiTypes, DbTechnology, DbTypeNames } from '../types.js'

export interface ConfigType {
	name: string
	properties: ConfigurationItemProperty[]
}

const passwordCredentialProperties = [
	new ConfigurationItemProperty({
		order: 1,
		name: 'userName',
		type: 'string',
		defaultExpression: `''`,
		isSecret: false,
		isAvailableOnClient: false,
	}),
	new ConfigurationItemProperty({
		order: 1,
		name: 'password',
		type: 'string',
		defaultExpression: `''`,
		isSecret: true,
		isAvailableOnClient: false,
	}),
]
const oauthCredentialProperties = [
	new ConfigurationItemProperty({
		order: 2,
		name: 'userName',
		type: 'string',
		defaultExpression: `''`,
		isSecret: false,
		isAvailableOnClient: false,
	}),
	new ConfigurationItemProperty({
		order: 2,
		name: 'oauthClientId',
		type: 'string',
		defaultExpression: `''`,
		isSecret: false,
		isAvailableOnClient: false,
	}),
	new ConfigurationItemProperty({
		order: 2,
		name: 'oauthClientSecret',
		type: 'string',
		defaultExpression: `''`,
		isSecret: true,
		isAvailableOnClient: false,
	}),
	new ConfigurationItemProperty({
		order: 2,
		name: 'oauthTenantId',
		type: 'string',
		defaultExpression: `''`,
		hint: 'Use for Microsoft 365 to automatically derive the authority URL',
		isSecret: false,
		isAvailableOnClient: false,
	}),
	new ConfigurationItemProperty({
		order: 2,
		name: 'oauthAuthorityUrl',
		type: 'string',
		defaultExpression: `''`,
		hint: 'Use for providers other than Microsoft 365',
		isSecret: false,
		isAvailableOnClient: false,
	}),
	new ConfigurationItemProperty({
		order: 2,
		name: 'oauthScopes',
		type: 'string',
		defaultExpression: `''`,
		hint: 'Use only if needed',
		isSecret: false,
		isAvailableOnClient: false,
	}),
]
const passwordOrOauthCredentialProperties = [
	...passwordCredentialProperties,
	...oauthCredentialProperties.filter((p) => p.name != 'userName'),
]
const connectionProperties = [
	new ConfigurationItemProperty({
		name: 'location',
		type: 'string',
		defaultExpression: `''`,
		isSecret: false,
		isAvailableOnClient: false,
		hint: 'Hostname / filename / URL, depending on the use of this configuration',
	}),
	new ConfigurationItemProperty({
		name: 'port',
		type: 'number',
		defaultExpression: `0`,
		isSecret: false,
		isAvailableOnClient: false,
		validationRegex: '^\\d+$',
		hint: 'Use 0 to choose default port',
	}),
]
const connectionWithPasswordCredentialProperties = [
	...connectionProperties,
	...passwordCredentialProperties,
].sort((a, b) => a.order - b.order)
const connectionWithOAuthCredentialProperties = [
	...connectionWithPasswordCredentialProperties,
	...oauthCredentialProperties,
].sort((a, b) => a.order - b.order)
const connectionWithPasswordOrOauthCredentialProperties = [
	...connectionProperties,
	...passwordOrOauthCredentialProperties,
].sort((a, b) => a.order - b.order)
const mailConnectionProperties = [
	...connectionWithPasswordOrOauthCredentialProperties,
	new ConfigurationItemProperty({
		name: 'smtpLocation',
		type: 'string',
		defaultExpression: `''`,
		isSecret: false,
		isAvailableOnClient: false,
		hint: 'Hostname / filename / URL, depending on the use of this configuration',
	}),
	new ConfigurationItemProperty({
		name: 'smtpPort',
		type: 'number',
		defaultExpression: `0`,
		isSecret: false,
		isAvailableOnClient: false,
		validationRegex: '^\\d+$',
		hint: 'Use 0 to choose default port 993',
	}),
	new ConfigurationItemProperty({
		order: 3,
		name: 'defaultFrom',
		type: 'string',
		defaultExpression: `''`,
		isSecret: false,
		isAvailableOnClient: false,
		hint: 'Use email address or "Name <name@example.com>"',
	}),
	new ConfigurationItemProperty({
		order: 3,
		name: 'isSendToMockRecipientsEnabled',
		type: 'boolean',
		defaultExpression: `true`,
		isSecret: false,
		isAvailableOnClient: false,
		hint: 'If true, mails are never sent out to the real TO/CC/BCC recipients',
	}),
	new ConfigurationItemProperty({
		order: 3,
		name: 'mockRecipientAddresses',
		type: 'string',
		defaultExpression: `''`,
		isSecret: false,
		isAvailableOnClient: false,
		hint: 'Comma-separated list of mock recipients',
	}),
].sort((a, b) => a.order - b.order)
const dbConnectionProperties = [
	new ConfigurationItemProperty({
		name: 'dbType',
		type: 'string',
		defaultExpression: `'postgres'`,
		isSecret: false,
		isAvailableOnClient: false,
		inputType: 'dropdown',
		selectionOptions: Object.keys(DbTypeNames).map(
			(key) =>
				new ConfigItemSelectionOption({
					value: key,
					displayName: DbTypeNames[key as keyof typeof DbTypeNames],
				}),
		),
	}),
	new ConfigurationItemProperty({
		name: 'dbName',
		type: 'string',
		defaultExpression: `''`,
		isSecret: false,
		isAvailableOnClient: false,
		hint: 'Name of the DB within the DB server',
	}),
	new ConfigurationItemProperty({
		name: 'dbSchema',
		type: 'string',
		defaultExpression: `''`,
		isSecret: false,
		isAvailableOnClient: false,
		hint: 'Schema within the DB',
	}),
	...connectionWithPasswordCredentialProperties,
]
const isAzureOpenAi = (value: any) => (value.aiType as AiType) == 'AzureOpenAi'
const aiConnectionProperties = [
	new ConfigurationItemProperty({
		name: 'aiType',
		type: 'Sys.$Types.AiType',
		isAvailableOnClient: false,
		inputType: 'dropdown',
		selectionOptions: AiTypes.map(
			(aiType) =>
				new ConfigItemSelectionOption({
					value: aiType,
					displayName: aiType,
				}),
		),
	}),
	new ConfigurationItemProperty({
		name: 'instanceName',
		type: 'string',
		isAvailableOnClient: false,
		defaultExpression: `''`,
		isApplicable: isAzureOpenAi,
	}),
	new ConfigurationItemProperty({
		name: 'deploymentName',
		type: 'string',
		isAvailableOnClient: false,
		defaultExpression: `''`,
		isApplicable: isAzureOpenAi,
	}),
	new ConfigurationItemProperty({
		name: 'embeddingsDeploymentName',
		type: 'string',
		isAvailableOnClient: false,
		defaultExpression: `''`,
		isApplicable: isAzureOpenAi,
	}),
	new ConfigurationItemProperty({
		name: 'apiKey',
		type: 'string',
		isAvailableOnClient: false,
		isSecret: true,
		defaultExpression: `''`,
	}),
	new ConfigurationItemProperty({
		name: 'apiVersion',
		type: 'string',
		isAvailableOnClient: false,
		defaultExpression: `''`,
		isApplicable: isAzureOpenAi,
	}),
	new ConfigurationItemProperty({
		name: 'tracingEnabled',
		type: 'boolean',
		isAvailableOnClient: false,
		defaultExpression: `false`,
	}),
	new ConfigurationItemProperty({
		name: 'tracingEndpoint',
		type: 'string',
		isAvailableOnClient: false,
		defaultExpression: `'https://api.smith.langchain.com'`,
	}),
	new ConfigurationItemProperty({
		name: 'tracingApiKey',
		type: 'string',
		isAvailableOnClient: false,
		isSecret: true,
		defaultExpression: `undefined`,
	}),
	new ConfigurationItemProperty({
		name: 'usdPer1000InputTokens',
		type: 'number | undefined',
		isAvailableOnClient: false,
		defaultExpression: `undefined`,
	}),
	new ConfigurationItemProperty({
		name: 'usdPer1000OutputTokens',
		type: 'number | undefined',
		isAvailableOnClient: false,
		defaultExpression: `undefined`,
	}),
]

const ebicsConnectionProperties = [
	new ConfigurationItemProperty({
		name: 'url',
		type: 'string',
		isAvailableOnClient: false,
	}),
	new ConfigurationItemProperty({
		name: 'hostId',
		type: 'string',
		isAvailableOnClient: false,
	}),
	new ConfigurationItemProperty({
		name: 'partnerId',
		type: 'string',
		isAvailableOnClient: false,
	}),
	new ConfigurationItemProperty({
		name: 'userId',
		type: 'string',
		isAvailableOnClient: false,
	}),
	new ConfigurationItemProperty({
		name: 'key',
		type: 'string',
		isAvailableOnClient: false,
		isSecret: true,
	}),
	new ConfigurationItemProperty({
		name: 'passphrase',
		type: 'string',
		isAvailableOnClient: false,
		isSecret: true,
	}),
	new ConfigurationItemProperty({
		name: 'iv',
		type: 'string',
		isAvailableOnClient: false,
		isSecret: true,
		defaultExpression: `undefined`,
	}),
]

const keypairProperties = [
	new ConfigurationItemProperty({
		name: 'publicKey',
		type: 'string',
		isAvailableOnClient: false,
		isSecret: true,
	}),
	new ConfigurationItemProperty({
		name: 'privateKey',
		type: 'string',
		isAvailableOnClient: false,
		isSecret: true,
	}),
	new ConfigurationItemProperty({
		name: 'passphrase',
		type: 'string',
		isAvailableOnClient: false,
		isSecret: true,
	}),
	new ConfigurationItemProperty({
		name: 'iv',
		type: 'string',
		isAvailableOnClient: false,
		isSecret: false,
	}),
]

export type ConfigTypeName =
	| 'blank'
	| 'credential'
	| 'connection'
	| 'connectionWithCredential'
	| 'keypair'
	| 'mailConnection'
	| 'dbConnection'
	| 'aiConnection'
	| 'ebicsConnection'
type ConfigTypes = Record<ConfigTypeName, ConfigType>
export const ConfigTypes: ConfigTypes = {
	blank: { name: 'Blank', properties: [] },
	credential: { name: 'Credential', properties: passwordCredentialProperties },
	connection: {
		name: 'Connection (anonymous)',
		properties: connectionProperties,
	},
	connectionWithCredential: {
		name: 'Connection (with credential)',
		properties: connectionWithPasswordCredentialProperties,
	},
	keypair: {
		name: 'Keypair (public and/or private)',
		properties: keypairProperties,
	},
	mailConnection: {
		name: 'Mail server connection',
		properties: mailConnectionProperties,
	},
	dbConnection: { name: 'DB connection', properties: dbConnectionProperties },
	aiConnection: { name: 'AI connection', properties: aiConnectionProperties },
	ebicsConnection: {
		name: 'EBICS connection',
		properties: ebicsConnectionProperties,
	},
}

export interface CredentialSetting {
	userName: string
	password: string
}
export interface ConnectionSetting {
	location: string
	port: number
}
export interface ConnectionSettingWithCredential
	extends ConnectionSetting, CredentialSetting {}

export interface DbConnectionSetting extends ConnectionSettingWithCredential {
	dbType: DbTechnology
	dbName: string
	dbSchema: string
}
